import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { ConfigurationService, PortalConfigurationService } from '@draeger-academy/common';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AutoCompleteRequestSourcesEnum, AutoCompleteThemeEnum } from '../auto-complete-search/enums/auto-complete.enums';
import { AutoCompleteConfigInterface } from '../auto-complete-search/interfaces/auto-complete.interfaces';
import { CookieConsentOverlayService } from '../cookie-consent-overlay/cookie-consent-overlay.service';

@Component({
    selector: 'da-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FooterComponent implements OnInit {
    @Input() portalImprintUrl: string;
    public legalData: any;
    public currentYear: number;
    public site: string;
    public requestUrl: string;
    public useAutocompleteV3: boolean;
    private _currentData = null;
    private _$doormatData: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);
    private _$showCookiesData: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);
    public currentPortal: string;
    private _$portalId: BehaviorSubject<string> = new BehaviorSubject(null);
    public get portalId$(): Observable<string> {
        return this._$portalId.asObservable();
    }
    private _$portalSiteSettings: BehaviorSubject<string> = new BehaviorSubject(null);
    public get portalSiteSettings$(): Observable<string> {
        return this._$portalSiteSettings.asObservable();
    }

    private _$footerData: BehaviorSubject<{}> = new BehaviorSubject<{}>(null);
    private _$isPartnerPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _$isNotCountrySelectionPage: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _portalId: string;
    private _portalPreferences: any;
    private _$showSearch: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showSearch$(): Observable<boolean> {
        return this._$showSearch.asObservable();
    }

    public configSearch: AutoCompleteConfigInterface = {
        context: AutoCompleteRequestSourcesEnum.SP,
        theme: AutoCompleteThemeEnum.FOOTER,
        minCharsTypeAhead: 3,
        debounceTime: 200,
        displayMaxResults: 5,
        maxRequestResults: 200,
        goToTarget: true,
        showAllResultsLink: true,
        targetUrl: ''
    };

    constructor(
        protected _ref: ChangeDetectorRef,
        public _configurationService: ConfigurationService,
        private _translateService: TranslateService,
        private _cookieConsentOverlayService: CookieConsentOverlayService,
        private _portalConfigurationService: PortalConfigurationService
    ) {}

    public get footerData$(): Observable<{}> {
        return this._$footerData.asObservable();
    }

    public get isPartnerPage$(): Observable<boolean> {
        return this._$isPartnerPage.asObservable();
    }

    public get doormatData$(): Observable<{}> {
        return this._$doormatData.asObservable();
    }

    public get showCookiesData$(): Observable<{}> {
        return this._$showCookiesData.asObservable();
    }

    public get isNotCountrySelectionPage$(): Observable<{}> {
        return this._$isNotCountrySelectionPage.asObservable();
    }

    public get claim(): string {
        return '';
    }

    public ngOnInit() {
        this.currentYear = new Date().getFullYear();
        this._$isPartnerPage.next(this._configurationService.isPartnerPage());
        const _isNoSelectionPage = !this._configurationService.isSelectionPage();
        this._$isNotCountrySelectionPage.next(_isNoSelectionPage);
        if (this._$isPartnerPage.getValue() === true) {
            this._configurationService.portalPreferences$.subscribe((config) => (this._portalPreferences = config));
            this._configurationService.currentPortal$.subscribe((id) => (this._portalId = id));
        }
        this._translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this._configurationService.loadConfiguration(this._configurationService.isPartnerPage()).then(() => {
                this._setFooterData();
            });
        });

        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    this._$portalId.next(portalId);
                    this.currentPortal = this._$portalId.getValue();
                    this._configurationService.getConfigPartnerByPortal().then((values) => {
                        if (values) {
                            const tempFeatures = values.filter((setting) => setting.key.toLowerCase() === 'features');
                            let hasCheckPrerequest;
                            if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                                hasCheckPrerequest = tempFeatures[0].value.filter((setting) => setting.name === 'ActiveApplications');
                            }
                            if (hasCheckPrerequest && hasCheckPrerequest[0] && hasCheckPrerequest[0].enabled) {
                                this._$showCookiesData.next(true);
                            }
                        }
                    });
                }
            });
        }

        if (this._configurationService.hasFeature('ActiveApplications') && _isNoSelectionPage) {
            this._$showCookiesData.next(true);
        }
        this._setFooterData();

        if (this._configurationService.hasFeature('SearchPage')) {
            this._$showSearch.next(true);
        }
    }

    public siteHasTranslationData(key: string) {
        return this._translateService.translations[this.site] && this._translateService.translations[this.site][key];
    }

    private _setFooterData() {
        if (this._configurationService.isPartnerPage()) {
            this._setPortalFooter();
        } else {
            this._setFooter();
        }
    }

    private _setFooter() {
        this._configurationService.$siteSettings.subscribe((data) => {
            if (data && data.length > 0 && JSON.stringify(this._currentData) !== JSON.stringify(data)) {
                const _siteFromUrl = this._configurationService.getSiteFromUrl();
                this.site = _siteFromUrl ? _siteFromUrl.site : '';
                this._currentData = data;
                const _footerData = data.filter((f) => f.key.toLowerCase() === 'footer')[0];
                if (_footerData) {
                    this._$footerData.next(_footerData['value'].bottomFooter);
                    this._$doormatData.next(_footerData['value'].topFooter);
                }
            }
        });
    }

    private _setPortalFooter() {
        this._translateService
            .stream(['footer', 'common'])
            .pipe(take(1))
            .subscribe((translations) => {
                if (translations) {
                    if (translations && typeof translations['footer'] !== 'string' && typeof translations['common'] !== 'string') {
                        const _footerData = translations['footer'];
                        const _preferences = this._portalPreferences;
                        const _disclaimerLabel = translations['common'].labelDisclaimer;
                        const _disclaimerTerms = translations['common'].labelTermsConditions;
                        _footerData.bottomFooter['links'] = [
                            { label: _disclaimerLabel, link: '/partner/' + this._portalId + '/disclaimer' },
                            { label: _disclaimerTerms, link: '/partner/' + this._portalId + '/terms' }
                        ];
                        if (_preferences.showRevocation && translations['common'].labelRevocation) {
                            _footerData.bottomFooter['links'].push({
                                label: translations['common'].labelRevocation,
                                link: '/partner/' + this._portalId + '/revocation'
                            });
                        }
                        if (_preferences.webPortalEmail && _preferences.webPortalEmail !== '') {
                            const _alreadyInObject =
                                JSON.stringify(_footerData.topFooter.columns).indexOf(_preferences.webPortalEmail) > -1 ||
                                JSON.stringify(_footerData.topFooter.columns).indexOf('E-Mail') > -1;
                            if (!_alreadyInObject) {
                                _footerData.topFooter.columns.push({
                                    title: 'E-Mail',
                                    text: _preferences.webPortalEmail,
                                    link: 'mailto:' + _preferences.webPortalEmail
                                });
                            }
                        }

                        this._$footerData.next(_footerData.bottomFooter);
                        this._$doormatData.next(_footerData.topFooter);
                    }
                }
            });
    }

    public openCookiesOverlay() {
        this._cookieConsentOverlayService.openCookieSettingsOverlayFromFooter();
    }

    public scrollToTop() {
        window.scroll({
            top: 0,
            left: 0,
            behavior: 'smooth'
        });
    }
}
